import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import { getConf, SaveTypes } from '@configuratorware/configurator-frontendgui/App/configuration';
import { getLocale, t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { getConfiguration, getConfigurationCode, getLanguages } from '../../Reducers/Configurator/Selectors';
import { fetchLanguages } from '../../Reducers/Configurator/Actions';
import omit from 'lodash/omit';

const flagContext = require.context('../../Resources/Images', false, /\.svg$/);

function getFlagSrc(iso: string): string | null {
  try {
    return flagContext('./' + iso + '.svg');
  } catch {
    return null;
  }
}

const useStyles = makeStyles(
  (theme) => ({
    flagButton: {
      padding: theme.spacing(0.5),
    },
    flagImage: {
      width: 24,
      height: 18,
      objectFit: 'cover',
      display: 'block',
    },
    flagImageFallback: {
      width: 24,
      height: 18,
      fontSize: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.secondary,
    },
    menuItemFlag: {
      width: 24,
      height: 18,
      marginRight: theme.spacing(1.5),
      objectFit: 'cover',
      flexShrink: 0,
    },
    menuItemFlagFallback: {
      width: 24,
      height: 18,
      minWidth: 24,
      marginRight: theme.spacing(1.5),
      fontSize: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.secondary,
      flexShrink: 0,
    },
    menuItemLabel: {
      fontFamily: 'Inter',
      fontSize: '0.875rem',
    },
    menuItemContent: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
  }),
  { name: 'LanguageSwitch' }
);

function localeToIso(locale: string): string {
  return locale.replace('-', '_');
}

function isConfiguratorPath(pathname: string): boolean {
  return /^\/(identifier:[^/]+|code:[^/]+)/.test(pathname);
}

function buildNewPath(pathname: string, code: string): string {
  return pathname.replace(/^\/(identifier:[^/]+|code:[^/]+)/, `/code:${code}`);
}

const LanguageSwitch: React.FC = () => {
  const classes = useStyles();
  const store = useStore();
  const dispatch = useDispatch();
  const configuration = useSelector(getConfiguration);
  const languages = useSelector(getLanguages);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (languages.length === 0) {
      dispatch(fetchLanguages());
    }
  }, [dispatch, languages.length]);

  const currentLocale = getLocale();
  const currentIso = localeToIso(currentLocale);

  const performLanguageSwitch = useCallback(
    async (newLangIso: string) => {
      if (newLangIso === currentIso) return;

      setLoading(true);
      setAnchorEl(null);

      const pathname = window.location.pathname;
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('_language', newLangIso);
      const newSearch = searchParams.toString();

      try {
        if (isConfiguratorPath(pathname) && configuration?.item?.identifier) {
          const configToSave = omit(configuration, ['code', 'date_created']);
          await Services.configurator.saveConfiguration(configToSave, SaveTypes.LANGUAGESWITCH);
          const savedCode = getConfigurationCode(store.getState());
          if (savedCode) {
            const newPath = buildNewPath(pathname, savedCode);
            const newUrl = `${window.location.origin}${newPath}${newSearch ? `?${newSearch}` : ''}`;
            window.location.assign(newUrl);
            return;
          }
        }

        const newUrl = `${window.location.origin}${pathname}${newSearch ? `?${newSearch}` : ''}`;
        window.location.assign(newUrl);
      } catch (err) {
        console.error('Language switch failed:', err);
        Services.ui.reportError(t('saveConfiguration.error.savingFailed'));
      } finally {
        setLoading(false);
      }
    },
    [currentIso, configuration, store]
  );

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (iso: string) => {
    performLanguageSwitch(iso);
  };

  if (!languages.length) return null;

  const currentFlagSrc = getFlagSrc(currentIso);

  return (
    <Box display="inline-flex" alignItems="center">
      <Tooltip title={t('general.language')} arrow>
        <IconButton
          className={classes.flagButton}
          onClick={handleOpen}
          disabled={loading}
          size="small"
          aria-label={t('general.language')}
          aria-haspopup="listbox"
          aria-expanded={Boolean(anchorEl)}
        >
          {currentFlagSrc ? (
            <img src={currentFlagSrc} alt="" className={classes.flagImage} />
          ) : (
            <span className={classes.flagImageFallback}>{currentIso.slice(0, 2).toUpperCase()}</span>
          )}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        getContentAnchorEl={null}
        MenuListProps={{ role: 'listbox' }}
      >
        {languages.filter((language: { iso: string }) => language.iso !== 'zh_CN').map((language: { iso: string }) => {
          const flagSrc = getFlagSrc(language.iso);
          return (
            <MenuItem
              key={language.iso}
              selected={language.iso === currentIso}
              onClick={() => handleSelect(language.iso)}
              role="option"
            >
              <div className={classes.menuItemContent}>
                {flagSrc ? (
                  <img src={flagSrc} alt="" className={classes.menuItemFlag} />
                ) : (
                  <span className={classes.menuItemFlagFallback}>
                    {language.iso.slice(0, 2).toUpperCase()}
                  </span>
                )}
                <Typography className={classes.menuItemLabel}>{t(language.iso) || language.iso}</Typography>
              </div>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default LanguageSwitch;
